import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Link } from "react-router-dom";
import API from "../../../api/Axios";
import DataLoader from "../../../shared/DataLoader/DataLoader";

const SHVRProgress: React.FC = () => {
  const { t } = useTranslation("home");
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [stateData, setStateData] = useState([]);
  const [stateDataLoading, setStateDataLoading] = useState(false);
  const [statesList, setStatesList] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [regionInfo, setRegionInfo] = useState<any>({});

  const handleStateChange = (event) => {
    setState(event.target.value);
  };

  const handleDistrictChange = (event) => {
    setDistrict(event.target.value);
  };

  const handleApplyFilter = () => {
    if (state) {
      getStateData(parseInt(state));
    }
  };

  const handleResetFilter = () => {
    if (state) {
      setState("");
      setDistrict("");
      getStateData();
    }
  };

  const getStatesList = async () => {
    try {
      setStatesLoading(true);
      const response = await API.get("master/master-states");
      console.log("master states", response);
      setStatesList(response?.data?.data || []);
    } catch (err) {
      console.log("error fetching states list", err);
      setStatesList([]);
    } finally {
      setStatesLoading(false);
    }
  };

  const getStateData = async (stateId?: number | null) => {
    try {
      setStateDataLoading(true);
      const url = stateId
        ? `web/survey-stats/state-wise?state_id=${stateId}`
        : "web/survey-stats/state-wise";
      const response = await API.get(url);
      setStateData(response?.data?.data?.region_stats);
      setRegionInfo(response?.data?.data?.region_info);
    } catch (err) {
      console.log("error fetching state data", err);
    } finally {
      setStateDataLoading(false);
    }
  };

  useEffect(() => {
    getStatesList();
    getStateData();
  }, []);

  return (
    <Box
      sx={{
        background: "transparent",
        pt: { xs: 3, md: "45px" },
        pb: { xs: 7, md: 10 },
        minHeight: "calc(100vh - 210px)",
      }}
    >
      <Container
        maxWidth={false}
        sx={{ width: { xs: "100%", sm: "94%", md: "88%" } }}
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <Box
              sx={{
                background: "transparent",
                pb: { xs: 1, md: 2 },
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                borderRadius: "8px",
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: isDarkMode ? theme.palette.text.primary : "#0F202B",
                  fontSize: {
                    xs: `calc(34px * var(--dynamic-font-size, 1)) !important`,
                    md: `calc(48px * var(--dynamic-font-size, 1)) !important`,
                  },
                  lineHeight: 1.15,
                  marginRight: 2,
                }}
              >
                {t("progress.title")}
              </Typography>

              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
                  sx={{ width: { xs: "100%", md: "auto" } }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: isDarkMode
                        ? theme.palette.text.primary
                        : "#0F202B",
                      fontWeight: 600,
                      fontSize: {
                        xs: `calc(14px * var(--dynamic-font-size, 1)) !important`,
                        md: `calc(16px * var(--dynamic-font-size, 1)) !important`,
                      },
                      mb: 1,
                    }}
                  >
                    {t("progress.filters")}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 1.5,
                      justifyContent: {
                        xs: "flex-end",
                        sm: "flex-end",
                        md: "unset",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      <FormControl
                        size="small"
                        sx={{
                          minWidth: { xs: "100%", sm: "200px" },
                          maxWidth: { xs: "100%", sm: "200px" },
                        }}
                      >
                        <InputLabel>{t("progress.selectState")}</InputLabel>
                        <Select
                          value={state}
                          onChange={handleStateChange}
                          label={t("progress.selectState")}
                          disabled={statesLoading}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                width: 300,
                              },
                            },
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left",
                            },
                            transformOrigin: {
                              vertical: "top",
                              horizontal: "left",
                            },
                          }}
                        >
                          {statesList.map((stateItem: any) => (
                            <MenuItem key={stateItem.id} value={stateItem.id}>
                              {stateItem.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                      <FormControl
                        size="small"
                        disabled
                        sx={{
                          minWidth: { xs: "100%", sm: "200px" },
                          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                        }}
                      >
                        <InputLabel>{t("progress.selectDistrict")}</InputLabel>
                        <Select
                          value={district}
                          onChange={handleDistrictChange}
                          label={t("progress.selectDistrict")}
                        >
                          <MenuItem value="">—</MenuItem>
                        </Select>
                      </FormControl>
                    </Box> */}

                    <Box>
                      <Button
                        variant="outlined"
                        onClick={handleApplyFilter}
                        sx={{
                          px: 3,
                          borderColor: isDarkMode ? "#9b9b9b" : "#263b80",
                          color: isDarkMode ? "#d0d0d0" : "#263b80",
                          borderRadius: "999px",
                          fontSize: "14px",
                          fontWeight: 500,
                          height: "40px",
                        }}
                      >
                        {t("progress.applyFilter")}
                      </Button>
                    </Box>

                    <Box>
                      <Button
                        variant="outlined"
                        onClick={handleResetFilter}
                        sx={{
                          px: 3,
                          borderColor: isDarkMode ? "#9b9b9b" : "#263b80",
                          color: isDarkMode ? "#d0d0d0" : "#263b80",
                          borderRadius: "999px",
                          fontSize: "14px",
                          fontWeight: 500,
                          height: "40px",
                        }}
                      >
                        {t("progress.reset")}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Box>
          </Grid>

          {/* Last Updated Display - Between Filter and Table */}
          {regionInfo?.updated_at && (
            <Grid size={12} sx={{ marginTop: 0, marginBottom: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  padding: { xs: "2px 0", md: "4px 0" },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? theme.palette.text.primary : "#374151",
                    fontSize: {
                      xs: `calc(12px * var(--dynamic-font-size, 1)) !important`,
                      md: `calc(14px * var(--dynamic-font-size, 1)) !important`,
                    },
                    fontWeight: 600,
                    fontFamily:
                      '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                  }}
                >
                  Last Updated:{" "}
                  {(() => {
                    const date = new Date(regionInfo?.updated_at);
                    const day = date.getDate().toString().padStart(2, "0");
                    const month = date.toLocaleDateString("en-US", {
                      month: "short",
                    });
                    const year = date.getFullYear().toString().slice(-2);
                    return `${day}-${month}-${year}`;
                  })()}
                  ,{" "}
                  {new Date(regionInfo?.updated_at).toLocaleTimeString(
                    "en-US",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    }
                  )}
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid
            size={12}
            sx={{
              boxShadow: "0px 1.24px 2.47px -1.24px #0000001A",
              marginTop: regionInfo?.updated_at ? 0 : 2,
              border: `1px solid ${isDarkMode ? "#5f6666" : "#5de4d2"}`,
              borderRadius: { xs: "24px", md: "44px" },
              overflow: "hidden",
              background: isDarkMode
                ? "linear-gradient(180deg, #292d2f 0%, #25322c 100%)"
                : "linear-gradient(180deg, rgba(255,255,255,.96) 0%, rgba(235,255,235,.78) 100%)",
            }}
          >
            {/* Table Header */}
            <Box
              sx={{
                padding: { xs: "15px", md: "25px" },
                background: "transparent",
                borderRadius: 0,
                borderBottom: "1px solid#F3F4F6",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: isDarkMode ? theme.palette.text.primary : "#1E2939",
                  fontSize: {
                    xs: `calc(16px * var(--dynamic-font-size, 1)) !important`,
                    md: `calc(20px * var(--dynamic-font-size, 1)) !important`,
                  },
                  mb: 0,
                }}
              >
                {t("progress.stateWiseProgressTracker")}
              </Typography>
            </Box>
            {stateDataLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "350px",
                  backgroundColor: isDarkMode
                    ? theme.palette.background.paper
                    : "#fcfdfd",
                  borderRadius: "0 0 12px 12px",
                }}
              >
                <DataLoader />
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 0,
                  padding: { xs: "10px 15px 22px", md: "0 25px 30px" },
                  background: "transparent",
                  overflowX: "auto",
                }}
              >
                <Table className="progress-status-table" sx={{ minWidth: 800 }}>
                  <TableHead
                    sx={{
                      "& .MuiTableCell-root": {
                        fontWeight: 700,
                        // backgroundColor: isDarkMode
                        //   ? theme.palette.background.paper
                        //   : "#fdfefe",
                        color: isDarkMode
                          ? theme.palette.text.primary
                          : "#364153",
                        fontSize: {
                          xs: `calc(12px * var(--dynamic-font-size, 1)) !important`,
                          md: `calc(17px * var(--dynamic-font-size, 1)) !important`,
                        },
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell>{t("progress.stateName")}</TableCell>
                      <TableCell>{t("progress.initiated")}</TableCell>
                      <TableCell>{t("progress.registered")}</TableCell>
                      <TableCell>{t("progress.completed")}</TableCell>
                      <TableCell>{t("progress.action")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stateData?.map((row: any, index: number) => {
                      console.log("row", row);
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                // backgroundColor: isDarkMode
                                //   ? theme.palette.background.paper
                                //   : "#fdfefe",
                                color: isDarkMode
                                  ? theme.palette.text.primary
                                  : "#364153",
                                fontSize: {
                                  xs: `calc(12px * var(--dynamic-font-size, 1)) !important`,
                                  md: `calc(17px * var(--dynamic-font-size, 1)) !important`,
                                },
                              }}
                            >
                              {row?.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row?.initiated}
                              sx={{
                                backgroundColor: isDarkMode
                                  ? theme.palette.warning.dark
                                  : "#FFEDD4",
                                color: isDarkMode
                                  ? theme.palette.warning.contrastText
                                  : "#CA3500",
                                fontWeight: 400,
                                borderRadius: "10px",
                                fontSize: { xs: "12px", md: "14px" },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row?.registered}
                              sx={{
                                backgroundColor: isDarkMode
                                  ? theme.palette.action.selected
                                  : "#F1F5F9",
                                color: isDarkMode
                                  ? theme.palette.text.primary
                                  : "#4A5565",
                                fontWeight: 400,
                                borderRadius: "10px",
                                fontSize: { xs: "12px", md: "14px" },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row?.completed}
                              sx={{
                                backgroundColor: isDarkMode
                                  ? theme.palette.success.dark
                                  : "#DCFCE7",
                                color: isDarkMode
                                  ? theme.palette.success.contrastText
                                  : "#008236",
                                fontWeight: 400,
                                borderRadius: "10px",
                                fontSize: { xs: "12px", md: "14px" },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {row.id == 41 ||
                            row.id == 42 ||
                            row.id == 43 ||
                            row.id == 44 ||
                            row.id == 45 ||
                            row.id == 46 ? (
                              <Box
                                sx={{ textAlign: "center", maxWidth: "150px" }}
                              >
                                --
                              </Box>
                            ) : (
                              <Button
                                variant="outlined"
                                startIcon={<VisibilityOutlinedIcon />}
                                sx={{
                                  textTransform: "none",
                                  borderColor: isDarkMode
                                    ? theme.palette.divider
                                    : "#E5E7EB",
                                  color: isDarkMode
                                    ? theme.palette.text.primary
                                    : "#000",
                                  borderRadius: "999px",
                                  fontSize: { xs: "12px", md: "14px" },
                                  minWidth: "150px",
                                }}
                                component={Link}
                                to={`/progress/district/${row?.id}`}
                              >
                                {t("progress.viewDistricts")}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Totals Row */}
                    {stateData && stateData.length > 0 && (
                      <TableRow
                        sx={{
                          backgroundColor: isDarkMode
                            ? theme.palette.action.hover
                            : "#F8FAFC",
                          borderTop: `2px solid ${
                            isDarkMode ? theme.palette.divider : "#E2E8F0"
                          }`,
                          "&:hover": {
                            backgroundColor: isDarkMode
                              ? theme.palette.action.hover
                              : "#F1F5F9",
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: isDarkMode
                                ? theme.palette.text.primary
                                : "#364153",
                              fontSize: {
                                xs: `calc(14px * var(--dynamic-font-size, 1)) !important`,
                                md: `calc(17px * var(--dynamic-font-size, 1)) !important`,
                              },
                            }}
                          >
                            Total
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={regionInfo?.initiated || 0}
                            sx={{
                              backgroundColor: isDarkMode
                                ? theme.palette.warning.dark
                                : "#FFEDD4",
                              color: isDarkMode
                                ? theme.palette.warning.contrastText
                                : "#CA3500",
                              fontWeight: 400,
                              borderRadius: "10px",
                              fontSize: { xs: "12px", md: "14px" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={regionInfo?.registered || 0}
                            sx={{
                              backgroundColor: isDarkMode
                                ? theme.palette.action.selected
                                : "#F1F5F9",
                              color: isDarkMode
                                ? theme.palette.text.primary
                                : "#4A5565",
                              fontWeight: 400,
                              borderRadius: "10px",
                              fontSize: { xs: "12px", md: "14px" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={regionInfo?.completed || 0}
                            sx={{
                              backgroundColor: isDarkMode
                                ? theme.palette.success.dark
                                : "#DCFCE7",
                              color: isDarkMode
                                ? theme.palette.success.contrastText
                                : "#008236",
                              fontWeight: 400,
                              borderRadius: "10px",
                              fontSize: { xs: "12px", md: "14px" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ textAlign: "center", maxWidth: "150px" }}>
                            <Typography
                              sx={{
                                fontWeight: 400,
                                color: isDarkMode
                                  ? theme.palette.text.secondary
                                  : "#64748B",
                                fontSize: {
                                  xs: `calc(11px * var(--dynamic-font-size, 1)) !important`,
                                  md: `calc(13px * var(--dynamic-font-size, 1)) !important`,
                                },
                              }}
                            >
                              --
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default SHVRProgress;
